import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { api, type Project, type ProjectTreeNode } from './api';
import { useAuth } from './auth';

/**
 * The Organization → Project → Sub-project hierarchy, loaded once and shared app-wide.
 *
 * Mounted above `<Routes>` rather than per-route (the way `KbTreeProvider` is), because the
 * sidebar renders it on every page and switching projects must not refetch it — the tree is the
 * navigation, so it has to outlive the thing being navigated.
 *
 * It also replaces the two separate `api.projects.list()` calls the sidebar's switcher and
 * `useCurrentProject` each used to make, which meant every KB page fetched the project list twice.
 *
 * Projects only. It used to also cache each project's knowledge-base outline for the sidebar,
 * which nested every project's pages under its row; the pages now render inside the KB view
 * (`KbTreeProvider` + `KbTreePanel`), so this holds the hierarchy people navigate by and nothing
 * about the documents inside it.
 */
export interface ProjectTreeValue {
  /** Root nodes. With one organization this is a single entry, but the shape doesn't assume it. */
  tree: ProjectTreeNode[];
  /** Depth-first flattening — what pickers and slug lookups read. */
  allProjects: ProjectTreeNode[];
  /** The organization: the root of the tree. Null while loading. */
  organization: ProjectTreeNode | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const ProjectTreeContext = createContext<ProjectTreeValue | null>(null);

export function flattenProjects(nodes: ProjectTreeNode[], out: ProjectTreeNode[] = []): ProjectTreeNode[] {
  for (const node of nodes) {
    out.push(node);
    flattenProjects(node.children, out);
  }
  return out;
}

/** Ancestor paths of a project path, nearest-last — mirrors the server's `ancestorPathsOf`. */
export function ancestorPathsOf(path: string): string[] {
  const parts = path.split('/');
  return parts.slice(0, -1).map((_, i) => parts.slice(0, i + 1).join('/'));
}

export function ProjectTreeProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const userId = user?.id ?? null;

  const [tree, setTree] = useState<ProjectTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTree = useCallback(() => {
    setLoading(true);
    api.projects.tree().then(
      (next) => { setTree(next); setError(null); },
      (e) => setError(e.message),
    ).finally(() => setLoading(false));
  }, []);

  /**
   * Fetch only with a session, and refetch when the signed-in user changes.
   *
   * The tree is per-user — you see the projects you are a member of — so it cannot be loaded
   * before there is a user to load it for. Mounted above `<Routes>`, this provider also covers
   * `/login`, where an unconditional fetch 401s and leaves `tree` empty with `loading` false for
   * the rest of the page's life: nothing refetched on login, so `OrganizationHome` read
   * `organization === null` and sent the user who had just signed in straight back to `/login`.
   *
   * Keying on the user id rather than a boolean is what makes signing in as someone else load
   * their tree instead of leaving the previous session's, and clearing on sign-out is why the
   * next login cannot flash the last user's projects.
   */
  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      setTree([]);
      setError(null);
      setLoading(false);
      return;
    }
    fetchTree();
  }, [authLoading, userId, fetchTree]);

  const reload = useCallback(() => { if (userId) fetchTree(); }, [userId, fetchTree]);

  const allProjects = useMemo(() => flattenProjects(tree), [tree]);

  const value = useMemo<ProjectTreeValue>(
    () => ({ tree, allProjects, organization: tree[0] ?? null, loading, error, reload }),
    [tree, allProjects, loading, error, reload],
  );

  return <ProjectTreeContext.Provider value={value}>{children}</ProjectTreeContext.Provider>;
}

export function useProjectTree(): ProjectTreeValue {
  const value = useContext(ProjectTreeContext);
  if (!value) throw new Error('useProjectTree must be used inside a <ProjectTreeProvider>');
  return value;
}

/** The levels a read from `project` reaches — self, ancestors and descendants. */
export function scopeOf(project: Project, all: ProjectTreeNode[]): ProjectTreeNode[] {
  const ancestors = ancestorPathsOf(project.path);
  return all.filter(
    (p) =>
      p.path === project.path ||
      ancestors.includes(p.path) ||
      p.path.startsWith(`${project.path}/`),
  );
}
